import React, { useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../context/AuthContext';
import { canWrite, roleLabel } from '../../utils/roles';
import { PERMISSION_ROLES } from '../../utils/permissionCatalog';
import {
  FIXED_SCHEDULE_DAYS,
  fixedScheduleToWeekdays,
  personSystemAlias,
  weekdaysToFixedSchedule,
} from '../../utils/personnel';
import './PersonnelPage.css';

const API = '/api/personnel';
const SCHEDULE_TYPES = [
  { value: 'variable', label: 'Variable' },
  { value: 'fixed', label: 'Fijo' },
];
const LICENSE_TYPES = ['NO TIENE', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const OPERATIONAL_ROLES = ['Administración', 'Logístico', 'Referente', 'Operador', 'Depo y Mant.'];

function normalize(str) {
  return str?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') || '';
}

function canonicalOperationalRole(value) {
  const normalized = normalize(value);
  if (!normalized) return '';
  if (normalized.includes('administracion')) return 'Administración';
  if (normalized.includes('logistic')) return 'Logístico';
  if (normalized.includes('referente')) return 'Referente';
  if (normalized.includes('operador')) return 'Operador';
  if (normalized.includes('depo') || normalized.includes('deposito') || normalized.includes('mant')) return 'Depo y Mant.';
  if (normalized === 'admin') return 'Administración';
  return value;
}

function csvToArray(value) {
  return String(value || '').split(',').map(s => s.trim()).filter(Boolean);
}

function arrayToCsv(value) {
  return Array.isArray(value) ? value.join(', ') : '';
}

function formatCedula(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  return digits.length > 7 ? `${digits.slice(0, 7)}-${digits.slice(7)}` : digits;
}

function formatReportDate(value) {
  const text = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return '';
  const [year, month, day] = text.split('-');
  return `${day}/${month}/${year}`;
}

function cleanPdfText(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function exportPersonnelReport(people) {
  const rows = [...people]
    .filter((person) => cleanPdfText(person.operational_role))
    .sort((a, b) => cleanPdfText(a.full_name).localeCompare(cleanPdfText(b.full_name), 'es'))
    .map((person) => [
      cleanPdfText([person.first_name, person.last_name].filter(Boolean).join(' ')),
      cleanPdfText(person.cedula),
      formatReportDate(person.health_card_expires_on),
      cleanPdfText(person.driver_license_type),
      formatReportDate(person.driver_license_expires_on),
    ]);

  if (!rows.length) {
    window.alert('No hay personal con rol operativo para exportar.');
    return;
  }

  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  const issued = new Date().toLocaleDateString('es-UY');

  doc.setFont('helvetica', 'bold').setFontSize(18).setTextColor('#1f242e');
  doc.text('Reporte de personal', margin, 44);
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor('#6a7180');
  doc.text(`Emitido: ${issued}`, pageWidth - margin, 44, { align: 'right' });

  autoTable(doc, {
    startY: 68,
    margin: { left: margin, right: margin },
    head: [['Nombre + apellido', 'Cédula', 'Vto. carné salud', 'Cat. libreta', 'Vto. libreta']],
    body: rows,
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 9.5,
      textColor: '#1f242e',
      lineColor: [208, 212, 218],
      lineWidth: 0.5,
      cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
      valign: 'middle',
    },
    headStyles: {
      fillColor: [242, 244, 247],
      textColor: '#555b68',
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 260 },
      1: { cellWidth: 120 },
      2: { cellWidth: 135 },
      3: { cellWidth: 100 },
      4: { cellWidth: 120 },
    },
  });

  doc.save(`reporte-personal-${new Date().toISOString().slice(0, 10)}.pdf`);
}

function inviteStatusLabel(status) {
  return {
    pending: 'Pendiente de envío',
    sent: 'Invitación enviada',
    expired: 'Invitación vencida',
  }[status] || '';
}

function biometricStatusLabel(status) {
  return status === 'ok' || status === 'linked' ? 'Vinculado' : 'No disponible';
}

function biometricStatusClass(status) {
  return status === 'ok' || status === 'linked' ? 'is-linked' : 'is-empty';
}

function FixedScheduleEditor({ value, disabled, onChange }) {
  const updateDay = (key, patch) => onChange({
    ...value,
    [key]: { ...value[key], ...patch },
  });

  return (
    <section className={`personnel-schedule${disabled ? ' is-disabled' : ''}`} aria-disabled={disabled}>
      <div className="personnel-schedule-heading">
        <div>
          <strong>Horario semanal fijo</strong>
          <span>Se usa como base al habilitar nuevas semanas del Plan semanal.</span>
        </div>
        {disabled && <span className="personnel-schedule-variable">Horario variable</span>}
      </div>
      <div className="personnel-schedule-table">
        <div className="personnel-schedule-row personnel-schedule-header" aria-hidden="true">
          <span>Día</span><span>Activo</span><span>Entrada</span><span>Salida</span><span>Actividad</span>
        </div>
        {FIXED_SCHEDULE_DAYS.map(({ key, label }) => {
          const day = value[key];
          const inactive = disabled || !day.enabled;
          return (
            <div className="personnel-schedule-row" key={key}>
              <strong>{label}</strong>
              <label className="personnel-schedule-check" title={`Usar horario fijo el ${label.toLowerCase()}`}>
                <input
                  type="checkbox"
                  checked={!!day.enabled}
                  disabled={disabled}
                  onChange={(event) => updateDay(key, { enabled: event.target.checked })}
                />
                <span>{day.enabled ? 'Sí' : 'No'}</span>
              </label>
              <input className="form-input" type="time" step="300" value={day.start} disabled={inactive} onChange={(event) => updateDay(key, { start: event.target.value })} aria-label={`Entrada ${label}`} />
              <input className="form-input" type="time" step="300" value={day.end} disabled={inactive} onChange={(event) => updateDay(key, { end: event.target.value })} aria-label={`Salida ${label}`} />
              <input className="form-input" value={day.activity} disabled={inactive} onChange={(event) => updateDay(key, { activity: event.target.value })} aria-label={`Actividad ${label}`} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function PersonModal({ person, onClose, onSaved, employeeView = false }) {
  const [form, setForm] = useState({
    first_name: person?.first_name || '',
    last_name: person?.last_name || '',
    email: person?.email || '',
    phone: person?.phone || '',
    cedula: person?.cedula || '',
    operational_role: person?.operational_role || '',
    access_enabled: !!person?.access_enabled,
    app_role: person?.app_role || 'logistico',
    nickname: person?.nickname || '',
    avatar_url: person?.avatar_url || '',
    is_sales_ref: !!person?.is_sales_ref,
    agreed_hours: person?.agreed_hours ?? '',
    schedule_type: person?.schedule_type || 'variable',
    fixed_schedule: fixedScheduleToWeekdays(person?.fixed_schedule),
    driver_license_type: person?.driver_license_type || 'NO TIENE',
    // Las columnas DATE llegan como ISO datetime ("...T00:00:00.000Z"); el input
    // type=date sólo entiende YYYY-MM-DD, así que recortamos.
    driver_license_expires_on: (person?.driver_license_expires_on || '').slice(0, 10),
    health_card_expires_on: (person?.health_card_expires_on || '').slice(0, 10),
    operation_categories: arrayToCsv(person?.operation_categories),
    local_password: '',
    local_password_confirmation: '',
  });
  const [activeTab, setActiveTab] = useState('principal');
  const [aliases, setAliases] = useState(arrayToCsv(person?.aliases));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCedula = (event) => setForm((form) => ({ ...form, cedula: formatCedula(event.target.value) }));
  const setChecked = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.checked }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { setError('El nombre es requerido'); return; }
    if (form.local_password && form.local_password.length < 8) {
      setError('La contraseña local debe tener al menos 8 caracteres');
      return;
    }
    if (form.local_password !== form.local_password_confirmation) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const method = person ? 'PUT' : 'POST';
      const url = person ? `${API}/${person.id}` : API;
      const payload = {
        ...form,
        agreed_hours: form.agreed_hours === '' ? null : form.agreed_hours,
        fixed_schedule: weekdaysToFixedSchedule(form.fixed_schedule),
        operation_categories: csvToArray(form.operation_categories),
        aliases: csvToArray(aliases),
      };
      delete payload.local_password;
      delete payload.local_password_confirmation;
      delete payload.biometric_status;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al guardar');
      if (form.local_password) {
        if (!data.user_id) throw new Error('Primero habilitá el acceso al sistema para definir una contraseña local');
        const passwordRes = await fetch(`/api/auth/users/${data.user_id}/password`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: form.local_password }),
        });
        const passwordData = await passwordRes.json();
        if (!passwordRes.ok) throw new Error(passwordData.error || 'No se pudo definir la contraseña');
      }
      onSaved(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 780 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{employeeView ? (person ? 'Editar empleado' : 'Nuevo empleado') : (person ? 'Editar persona' : 'Nueva persona')}</h3>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} lang="es">
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {employeeView && <p className="text-dim">Los campos marcados con * son obligatorios.</p>}
            {error && <div className="alert alert-error">{error}</div>}
            <div className="personnel-modal-tabs" role="tablist" aria-label="Secciones de la ficha">
              <button type="button" className={activeTab === 'principal' ? 'is-active' : ''} onClick={() => setActiveTab('principal')}>Principal</button>
              <button type="button" className={activeTab === 'secundaria' ? 'is-active' : ''} onClick={() => setActiveTab('secundaria')}>Acceso y biometría</button>
            </div>
            {activeTab === 'principal' && <>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">ID RRHH</label>
                <input
                  className="form-input"
                  value={person?.private_code || 'Automático'}
                  readOnly
                  aria-readonly="true"
                  title={person ? 'Identificador generado por el sistema' : 'Se asigna al guardar'}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.first_name} onChange={set('first_name')} placeholder="Ej: Juan" autoFocus spellCheck={false} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Apellido</label>
                <input className="form-input" value={form.last_name} onChange={set('last_name')} placeholder="Ej: Pérez" spellCheck={false} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(160px, .45fr)', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="juan@ejemplo.com" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Cédula</label>
                <input className="form-input" value={form.cedula} onChange={setCedula} placeholder="4940819-2" maxLength={9} inputMode="text" autoComplete="off" />
              </div>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Teléfono</label>
              <input className="form-input" value={form.phone} onChange={set('phone')} placeholder="+598 99 000 000" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Rol operativo</label>
              <input className="form-input" value={form.operational_role} onChange={set('operational_role')} placeholder="Ej: Técnico, Coordinador…" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Alias / apodo del sistema</label>
                <input className="form-input" value={form.nickname} onChange={set('nickname')} placeholder="Ej: FEDE" spellCheck={false} />
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 42, color: 'var(--text-dim)', fontSize: 13 }}>
                <input type="checkbox" checked={form.is_sales_ref} onChange={setChecked('is_sales_ref')} />
                Referente de ventas
              </label>
            </div>
            </>}
            {activeTab === 'secundaria' && <>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 12 }}>
                <input type="checkbox" checked={form.access_enabled} onChange={setChecked('access_enabled')} />
                Habilitar acceso al sistema
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Acceso al sistema</label>
                  <select className="form-input" value={form.app_role} onChange={set('app_role')} disabled={!form.access_enabled}>
                    {PERMISSION_ROLES.map(role => <option key={role.key} value={role.key}>{role.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Avatar URL</label>
                  <input className="form-input" value={form.avatar_url} onChange={set('avatar_url')} placeholder="https://..." spellCheck={false} />
                </div>
              </div>
              {form.access_enabled && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Contraseña local</label>
                    <input className="form-input" type="password" value={form.local_password} onChange={set('local_password')} placeholder="Dejar vacío para no modificar" autoComplete="new-password" />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Confirmar contraseña</label>
                    <input className="form-input" type="password" value={form.local_password_confirmation} onChange={set('local_password_confirmation')} placeholder="Repetir contraseña" autoComplete="new-password" />
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Biometría</label>
                <div className="personnel-biometric-info">
                  <span className={`personnel-biometric-badge ${biometricStatusClass(person?.biometric_status)}`}>
                    {biometricStatusLabel(person?.biometric_status)}
                  </span>
                  <small>Se gestiona desde Configuración → Biometría.</small>
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Categorías de operación</label>
                <input
                  className="form-input"
                  value={form.operation_categories}
                  onChange={set('operation_categories')}
                  placeholder="Ej: Eventos, Depósito, Técnica"
                  spellCheck={false}
                />
              </div>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Nombres alternativos de importación</label>
              <input
                className="form-input"
                value={aliases}
                onChange={(event) => setAliases(event.target.value)}
                placeholder="Ej: Angie, Ange (separados por coma)"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
              />
              <span style={{ fontSize: 11, color: 'var(--text-mute)', marginTop: 3 }}>
                Sólo se usan para reconocer nombres históricos o importados. El alias visible del sistema se configura en Principal.
              </span>
            </div>
            </>}
            {activeTab === 'principal' && <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Horas acordadas</label>
                <input className="form-input" type="number" min="0" step="0.5" value={form.agreed_hours} onChange={set('agreed_hours')} placeholder="190" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Horario</label>
                <select className="form-input" value={form.schedule_type} onChange={set('schedule_type')}>
                  {SCHEDULE_TYPES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </div>
            </div>
            <FixedScheduleEditor
              value={form.fixed_schedule}
              disabled={form.schedule_type !== 'fixed'}
              onChange={(fixed_schedule) => setForm((current) => ({ ...current, fixed_schedule }))}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Libreta</label>
                <select className="form-input" value={form.driver_license_type} onChange={set('driver_license_type')}>
                  {LICENSE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Vence libreta</label>
                <input className="form-input" type="date" value={form.driver_license_expires_on || ''} onChange={set('driver_license_expires_on')} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Vence carné salud</label>
                <input className="form-input" type="date" value={form.health_card_expires_on || ''} onChange={set('health_card_expires_on')} />
              </div>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Categorías de operación</label>
              <input
                className="form-input"
                value={form.operation_categories}
                onChange={set('operation_categories')}
                placeholder="Ej: Eventos, Depósito, Técnica (separadas por coma)"
                spellCheck={false}
              />
            </div>
            </>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : person ? 'Guardar cambios' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PersonRow({ person, isAdmin, selected, operationalRoles, onEdit, onToggle, onQuickUpdate, onToggleSelection }) {
  const accessValue = person.access_enabled ? (person.app_role || 'logistico') : '';
  const operationalRole = canonicalOperationalRole(person.operational_role);

  return (
    <div className={`personnel-grid-row${person.active ? '' : ' is-inactive'}`} role="row">
      <div className="personnel-grid-cell personnel-selection-cell" role="cell">
        {isAdmin && <input type="checkbox" checked={selected} onChange={() => onToggleSelection(person.id)} aria-label={`Seleccionar a ${person.full_name}`} />}
      </div>
      <div className="personnel-grid-cell personnel-name-cell" role="cell"><strong>{person.first_name || '—'}</strong></div>
      <div className="personnel-grid-cell" role="cell">{person.last_name || '—'}</div>
      <div className="personnel-grid-cell personnel-email-cell" role="cell">{person.email || '—'}</div>
      <div className="personnel-grid-cell" role="cell">
        {isAdmin ? (
          <select
            className="personnel-inline-select"
            value={operationalRole}
            onChange={(event) => onQuickUpdate(person, { operational_role: canonicalOperationalRole(event.target.value) })}
            aria-label={`Rol operativo de ${person.full_name}`}
          >
            <option value="">Sin rol</option>
            {operationalRoles.map((role) => <option key={role} value={role}>{role}</option>)}
          </select>
        ) : (person.operational_role || '—')}
      </div>
      <div className="personnel-grid-cell personnel-access-cell" role="cell">
        {isAdmin ? (
          <div className="personnel-access-control">
            <select
              className="personnel-inline-select"
              value={accessValue}
              onChange={(event) => onQuickUpdate(person, event.target.value
                ? { access_enabled: true, app_role: event.target.value }
                : { access_enabled: false, app_role: null })}
              aria-label={`Acceso al sistema de ${person.full_name}`}
            >
              <option value="">Sin acceso</option>
              {PERMISSION_ROLES.map((role) => <option key={role.key} value={role.key}>{role.label}</option>)}
            </select>
            {inviteStatusLabel(person.access_invite_status) && <span className="personnel-invite-status">{inviteStatusLabel(person.access_invite_status)}</span>}
          </div>
        ) : <span>{person.access_enabled ? roleLabel(person.app_role) : 'Sin acceso'}</span>}
      </div>
      <div className="personnel-grid-cell personnel-alias-cell" role="cell"><strong>{personSystemAlias(person) || '—'}</strong></div>
      <div className="personnel-grid-cell personnel-biometric-cell" role="cell">
        <span className={`personnel-biometric-badge ${biometricStatusClass(person.biometric_status)}`}>
          {biometricStatusLabel(person.biometric_status)}
        </span>
      </div>
      <div className="personnel-grid-cell personnel-schedule-cell" role="cell">
        <span className={`personnel-schedule-badge is-${person.schedule_type === 'fixed' ? 'fixed' : 'variable'}`}>
          {person.schedule_type === 'fixed' ? 'Fijo' : 'Variable'}
        </span>
      </div>
      <div className="personnel-grid-cell personnel-actions-cell" role="cell">
        {isAdmin && (
          <div className="personnel-row-actions">
          <button
            className="btn btn-icon btn-ghost"
            title="Editar"
            onClick={() => onEdit(person)}
            style={{ minHeight: 36, minWidth: 36 }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button
            className="btn btn-icon btn-ghost"
            title={person.active ? 'Desactivar' : 'Activar'}
            onClick={() => onToggle(person)}
            style={{ minHeight: 36, minWidth: 36 }}
          >
            {person.active ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
              </svg>
            )}
          </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HRPage() {
  const { user } = useAuth();
  const isAdmin = canWrite(user, 'configuracion.personal');

  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sendingInvites, setSendingInvites] = useState(false);

  // Guard last-wins: togglear "Mostrar inactivos" rápido dispara loads que se
  // solapan; sin esto una respuesta vieja puede pisar a la nueva.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const res = await fetch(`${API}${showInactive ? '?all=1' : ''}`);
      const data = await res.json();
      if (seq !== loadSeq.current) return;
      setPeople(Array.isArray(data) ? data : []);
    } catch {
      if (seq === loadSeq.current) setPeople([]);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  // Tiempo real: el sync de RRHH emite 'personnel:updated' — recargar la lista
  // para no quedar stale (y que un "Guardar" del modal no pise lo que trajo el
  // sync). loadRef mantiene el efecto estable pese a que load cambie con showInactive.
  const loadRef = useRef();
  loadRef.current = load;
  useEffect(() => {
    const socket = io('/', { withCredentials: true });
    let t = null;
    const reload = () => { clearTimeout(t); t = setTimeout(() => loadRef.current?.(), 400); };
    socket.on('personnel:updated', reload);
    return () => { clearTimeout(t); socket.disconnect(); };
  }, []);

  const filtered = people.filter(p => {
    const q = normalize(search);
    return !q
      || normalize(p.full_name).includes(q)
      || normalize(p.email).includes(q)
      || normalize(p.operational_role).includes(q)
      || normalize(p.app_role).includes(q)
      || normalize(p.nickname).includes(q)
      || normalize(p.private_code).includes(q);
  });

  const operationalRoles = OPERATIONAL_ROLES;

  const handleSaved = (saved) => {
    setPeople(prev => {
      const idx = prev.findIndex(p => p.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next.sort((a, b) => a.full_name.localeCompare(b.full_name));
      }
      return [...prev, saved].sort((a, b) => a.full_name.localeCompare(b.full_name));
    });
    setShowModal(false);
    setEditing(null);
  };

  const handleToggle = async (person) => {
    handleQuickUpdate(person, { active: !person.active }, 'No se pudo cambiar el estado');
  };

  const handleQuickUpdate = async (person, patch, fallbackError = 'No se pudo guardar el cambio') => {
    try {
      const res = await fetch(`${API}/${person.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (res.ok) handleSaved(data);
      else alert(data.error || fallbackError);
    } catch {
      alert('Error de red al guardar el cambio');
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const visibleIds = filtered.map((person) => person.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((current) => {
      const next = new Set(current);
      visibleIds.forEach((id) => allVisibleSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const sendInvites = async () => {
    const ids = [...selectedIds];
    if (!ids.length || sendingInvites) return;
    const selectedPeople = people.filter((person) => selectedIds.has(person.id));
    if (!window.confirm(`Se enviará una invitación para definir contraseña a ${selectedPeople.length} ${selectedPeople.length === 1 ? 'persona' : 'personas'}. Las invitaciones anteriores pendientes se reemplazarán.`)) return;
    setSendingInvites(true);
    try {
      const response = await fetch(`${API}/access-invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personnel_ids: ids }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudieron enviar las invitaciones');
      const sent = data.results?.filter((result) => result.status === 'sent') || [];
      const failed = data.results?.filter((result) => result.status === 'error') || [];
      const details = failed.map((result) => result.error).filter(Boolean).join('\n');
      alert(`${sent.length} ${sent.length === 1 ? 'invitación enviada' : 'invitaciones enviadas'}${failed.length ? `\n${failed.length} sin enviar.${details ? `\n${details}` : ''}` : ''}`);
      setSelectedIds(new Set());
      load();
    } catch (error) {
      alert(error.message);
    } finally {
      setSendingInvites(false);
    }
  };

  const openEdit = (person) => { setEditing(person); setShowModal(true); };
  const openNew = () => { setEditing(null); setShowModal(true); };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Personal</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={() => exportPersonnelReport(filtered)} disabled={loading || filtered.length === 0}>
            Reporte de personal
          </button>
          {isAdmin && (
            <button className="btn btn-primary" onClick={openNew}>
              + Nuevo
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 200, maxWidth: 360 }}
          placeholder="Buscar por nombre, rol o email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Mostrar inactivos
          </label>
        )}
        <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          {filtered.length} {filtered.length === 1 ? 'persona global' : 'personas globales'}
        </span>
        {isAdmin && (
          <button className="btn btn-secondary" onClick={sendInvites} disabled={!selectedIds.size || sendingInvites}>
            {sendingInvites ? 'Enviando…' : `Enviar accesos${selectedIds.size ? ` (${selectedIds.size})` : ''}`}
          </button>
        )}
      </div>

      <div className="personnel-grid-shell" role="table" aria-label="Personal global">
        <div className="personnel-grid-row personnel-grid-header" role="row">
          <div role="columnheader" className="personnel-selection-cell">
            {isAdmin && <input type="checkbox" checked={filtered.length > 0 && filtered.every((person) => selectedIds.has(person.id))} onChange={toggleAllVisible} aria-label="Seleccionar todo lo visible" />}
          </div>
          <div role="columnheader">Nombre</div>
          <div role="columnheader">Apellido</div>
          <div role="columnheader">Mail</div>
          <div role="columnheader">ROL OPERATIVO</div>
          <div role="columnheader">ACCESO AL SISTEMA</div>
          <div role="columnheader">Alias</div>
          <div role="columnheader">Biometría</div>
          <div role="columnheader">Horario</div>
          <div role="columnheader" aria-label="Acciones" />
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-dim)' }}>
            {search ? 'Sin resultados.' : 'No hay personal registrado.'}
          </div>
        ) : (
          filtered.map(p => (
            <PersonRow
              key={p.id}
              person={p}
              isAdmin={isAdmin}
              selected={selectedIds.has(p.id)}
              operationalRoles={operationalRoles}
              onEdit={openEdit}
              onToggle={handleToggle}
              onQuickUpdate={handleQuickUpdate}
              onToggleSelection={toggleSelection}
            />
          ))
        )}
      </div>

      {showModal && (
        <PersonModal
          person={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
